import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import { parseConfigurationEventFrame } from "./configuration-events-frame";
import { permissionIdentity } from "../global/permissions/permission-identity";

export type ConfigurationSnapshot = {
  configuration_id: string;
  current_hash: string;
  latest_build_hash: string;
  latest_build_version: number;
};
type Listener = (event: string, data: any) => void;
type Connection = {
  listeners: Set<Listener>;
  controller: AbortController;
  snapshot?: ConfigurationSnapshot;
  state?: string;
};
const connections = new Map<string, Connection>();

async function consume(
  url: string,
  headers: Record<string, string>,
  connection: Connection,
) {
  const { signal } = connection.controller;
  let delay = 1000;
  while (!signal.aborted) {
    let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
    try {
      const response = await fetch(url, { headers, signal, cache: "no-store" });
      if ([401, 403, 404].includes(response.status)) {
        connection.state = "unavailable";
        connection.listeners.forEach((listener) =>
          listener("unavailable", null),
        );
        return;
      }
      if (
        !response.ok ||
        !response.headers.get("content-type")?.includes("text/event-stream") ||
        !response.body
      )
        throw new Error("Events unavailable");
      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      delay = 1000;
      while (!signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r/g, "");
        if (buffer.length > 1024 * 1024) throw new Error("Oversized event");
        let end: number;
        while ((end = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, end);
          buffer = buffer.slice(end + 2);
          const parsed = parseConfigurationEventFrame(frame);
          if (!parsed) continue;
          const { event, data } = parsed;
          try {
            if (event === "snapshot") connection.snapshot = data;
            if (["snapshot", "ready", "snapshot.error"].includes(event))
              connection.state = event;
            connection.listeners.forEach((listener) => listener(event, data));
          } catch {
            /* Malformed frames must not discard subsequent events. */
          }
        }
      }
    } catch {
      /* Reconnect to receive a fresh authoritative snapshot. */
    } finally {
      await reader?.cancel().catch(() => {});
    }
    if (signal.aborted) break;
    connection.state = "reconnecting";
    connection.listeners.forEach((listener) => listener("reconnecting", null));
    await new Promise<void>((resolve) => {
      const finish = () => {
        clearTimeout(timer);
        signal.removeEventListener("abort", finish);
        resolve();
      };
      const timer = setTimeout(finish, delay);
      signal.addEventListener("abort", finish, { once: true });
    });
    delay = Math.min(delay * 2, 30000);
  }
}

/** Shared authenticated stream; tokens never appear in URLs or access logs. */
export function useConfigurationEvents(
  configId?: string,
  configType = "agent",
  onRefresh?: (topic: string) => void,
) {
  const { token, tenantName } = useSelector((state: RootState) => state.auth);
  const { project_key, environment } = useSelector(
    (state: RootState) => state.appstate,
  );
  const [snapshot, setSnapshot] = useState<ConfigurationSnapshot | null>(null);
  const [connectionState, setConnectionState] = useState("connecting");
  const refreshRef = useRef(onRefresh);
  const scope = JSON.stringify([permissionIdentity(tenantName, token), configId, configType, project_key, environment]);
  const previousScope = useRef<string | null>(null);
  const latestSnapshot = useRef<ConfigurationSnapshot | null>(null);
  refreshRef.current = onRefresh;
  useEffect(() => {
    const sameScope = previousScope.current === scope;
    previousScope.current = scope;
    if (!sameScope || !token) {
      latestSnapshot.current = null;
      setSnapshot(null);
      setConnectionState("connecting");
    }
    if (!configId || !token || !tenantName || !project_key || !environment)
      return;
    const base = String(
      import.meta.env.PUBLIC_BASE_URL || window.location.origin,
    ).replace(/\/$/, "");
    const url = `${base}/api/${encodeURIComponent(tenantName)}/agent-configuration/${encodeURIComponent(configType)}/config/${encodeURIComponent(configId)}/events`;
    const key = JSON.stringify([url, token, project_key, environment]);
    let connection = connections.get(key);
    const isNew = !connection;
    if (!connection) {
      connection = { listeners: new Set(), controller: new AbortController() };
      connections.set(key, connection);
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const topics = new Set<string>();
    const listener: Listener = (event, data) => {
      const snapshotChanged = event === "snapshot" &&
        JSON.stringify(latestSnapshot.current) !== JSON.stringify(data);
      if (event === "snapshot") {
        latestSnapshot.current = data;
        setSnapshot(data);
        setConnectionState("live");
      }
      if (event === "ready") setConnectionState("live");
      if (["snapshot.error", "unavailable", "reconnecting"].includes(event))
        setConnectionState(event);
      if (
        (event === "snapshot" && (!sameScope || snapshotChanged)) ||
        (event === "ready" && !sameScope) ||
        event === "refresh.required"
      ) {
        topics.add(event === "refresh.required" ? data.topic : "snapshot");
        if (!timer)
          timer = setTimeout(() => {
            timer = undefined;
            const pending = [...topics];
            topics.clear();
            pending.forEach((topic) => refreshRef.current?.(topic));
          }, 200);
      }
    };
    connection.listeners.add(listener);
    if (connection.snapshot) listener("snapshot", connection.snapshot);
    if (connection.state && connection.state !== "snapshot")
      listener(connection.state, null);
    if (isNew)
      void consume(
        url,
        {
          Authorization: `Bearer ${token}`,
          ProjectKey: project_key,
          EnvironmentName: environment,
          Accept: "text/event-stream",
        },
        connection,
      );
    return () => {
      clearTimeout(timer);
      connection.listeners.delete(listener);
      if (!connection.listeners.size) {
        connection.controller.abort();
        connections.delete(key);
      }
    };
  }, [configId, configType, token, tenantName, project_key, environment, scope]);
  return { snapshot, connectionState };
}
